/**
 * Faixa da sessão da plataforma (#430).
 * sticky top-0: ocupa espaço no fluxo e fica no topo ao scroll; a altura vai
 * para --impersonation-banner-h (gaveta, overlay, modais, reabrir).
 * O texto só muda 3 vezes: início, faltam 5 min, terminou.
 * Anúncios vão só para ImpersonationAnnouncer (sem role=status aqui).
 */
import { useEffect, useRef, useState, type RefObject } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, toast } from '../ui/index.js'
import { impersonate } from '../../lib/api.js'
import { setToken } from '../../lib/auth.js'
import {
  clearImpersonationSession,
  getExpiredNotice,
  getImpersonationSession,
  markImpersonationExpired,
  onImpersonationChange,
  type ImpersonationSession,
} from '../../lib/impersonation.js'
import { announceImpersonation, requestFocusPageH1 } from '../../lib/impersonation-a11y.js'
import { useI18n } from '../../lib/i18n.js'
import { intlLocaleFor } from '@self-restaurant/shared'

const FIVE_MIN = 5 * 60
const BANNER_H_VAR = '--impersonation-banner-h'

function formatEndTime(expiresAt: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(expiresAt * 1000))
}

const RESTAURANT_MARK = '\uE000'

/** Nome do restaurante em <bdi> (RTL) dentro da frase i18n. */
function BannerLine({ phrase, restaurant }: { phrase: string; restaurant: string }) {
  const i = phrase.indexOf(RESTAURANT_MARK)
  if (i < 0) return <>{phrase}</>
  return (
    <>
      {phrase.slice(0, i)}
      <bdi>{restaurant}</bdi>
      {phrase.slice(i + RESTAURANT_MARK.length)}
    </>
  )
}

function useBannerHeight(ref: RefObject<HTMLDivElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) {
      document.documentElement.style.setProperty(BANNER_H_VAR, '0px')
      return
    }
    const el = ref.current
    if (!el) {
      document.documentElement.style.setProperty(BANNER_H_VAR, '0px')
      return
    }
    const setH = () => {
      document.documentElement.style.setProperty(
        BANNER_H_VAR,
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      )
    }
    setH()
    const ro = new ResizeObserver(setH)
    ro.observe(el)
    return () => {
      ro.disconnect()
      document.documentElement.style.setProperty(BANNER_H_VAR, '0px')
    }
  }, [ref, active])
}

export function ImpersonationBanner() {
  const { t, lang } = useI18n()
  const navigate = useNavigate()
  const bannerRef = useRef<HTMLDivElement>(null)
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    getImpersonationSession(),
  )
  /**
   * Cópia local ao expirar: markImpersonationExpired limpa o storage no mesmo
   * lote que setPhase('expired'); sem isto o render saía em `!session` e a
   * faixa «terminou» nunca aparecia (QA review 5638647805).
   */
  const [expiredSession, setExpiredSession] = useState<ImpersonationSession | null>(null)
  const [stopping, setStopping] = useState(false)
  const [phase, setPhase] = useState<'active' | 'soon' | 'expired'>('active')
  const announcedPhase = useRef<'active' | 'soon' | 'expired' | null>(null)
  const intlLocale = intlLocaleFor(lang)

  const showBanner = phase === 'expired' || !!expiredSession || !!session
  useBannerHeight(bannerRef, showBanner)

  useEffect(() => {
    const sync = () => {
      const next = getImpersonationSession()
      setSession(next)
      if (next) {
        setExpiredSession(null)
        setPhase('active')
        return
      }
      // Aviso em / dispensado e sem sessão → esconde a faixa «terminou»
      if (!getExpiredNotice()) {
        setExpiredSession(null)
        setPhase((p) => (p === 'expired' ? 'active' : p))
      }
    }
    return onImpersonationChange(sync)
  }, [])

  useEffect(() => {
    if (!session) return
    const tick = () => {
      const now = Math.floor(Date.now() / 1000)
      if (session.expires_at <= now) {
        setExpiredSession(session)
        setPhase('expired')
        markImpersonationExpired(now)
        navigate('/')
        requestFocusPageH1()
        return
      }
      setPhase(session.expires_at - now <= FIVE_MIN ? 'soon' : 'active')
    }
    tick()
    // Sem contador ao segundo — só reavalia a cada 15s (e ao montar)
    const id = window.setInterval(tick, 15_000)
    return () => window.clearInterval(id)
  }, [session, navigate])

  const time = session
    ? formatEndTime(session.expires_at, intlLocale)
    : expiredSession
      ? formatEndTime(expiredSession.expires_at, intlLocale)
      : ''

  // Anúncios: live region já no DOM; só 3 textos (início, 5 min, fim)
  useEffect(() => {
    if (phase === 'expired') {
      if (announcedPhase.current === 'expired') return
      announcedPhase.current = 'expired'
      announceImpersonation(t('admin.impersonation.expired'))
      return
    }
    if (!session) {
      announcedPhase.current = null
      return
    }
    if (phase === 'soon') {
      if (announcedPhase.current === 'soon' || announcedPhase.current === 'expired') return
      announcedPhase.current = 'soon'
      announceImpersonation(t('admin.impersonation.banner_soon', { time }))
      return
    }
    if (announcedPhase.current === 'active') return
    announcedPhase.current = 'active'
    announceImpersonation(
      t('admin.impersonation.banner', {
        restaurant: session.tenant_name,
        time,
      }),
    )
  }, [phase, session, t, time])

  async function handleStop() {
    setStopping(true)
    try {
      const data = await impersonate.stop()
      setToken(data.access_token)
      clearImpersonationSession()
      setExpiredSession(null)
      setPhase('active')
      navigate('/')
      requestFocusPageH1()
    } catch {
      // Nunca mensagem técnica da API / rede — só a chave i18n
      toast.error(t('admin.impersonation.end_err'))
    } finally {
      setStopping(false)
    }
  }

  const isExpired = phase === 'expired' || !!expiredSession
  // Faixa expired mesmo com session já limpa (expiredSession)
  if (!isExpired && !session) return null

  const activeSession = isExpired ? null : session
  const regionLabel = activeSession
    ? t('admin.impersonation.banner', {
        restaurant: activeSession.tenant_name,
        time,
      })
    : t('admin.impersonation.expired')

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label={regionLabel}
      data-testid="impersonation-banner"
      className="sticky top-0 z-[35] flex flex-wrap items-center gap-3 bg-pratzu-aubergine px-4 py-3 text-pratzu-cream"
    >
      {activeSession ? (
        <>
          <div className="basis-full sm:basis-0 flex-1 min-w-0 text-sm text-start space-y-0.5">
            <p className="break-words">
              {phase === 'soon' ? (
                t('admin.impersonation.banner_soon', { time })
              ) : (
                <BannerLine
                  phrase={t('admin.impersonation.banner', {
                    restaurant: RESTAURANT_MARK,
                    time,
                  })}
                  restaurant={activeSession.tenant_name}
                />
              )}
            </p>
            <p
              className="text-pratzu-cream/90 line-clamp-2 break-words"
              title={activeSession.reason}
            >
              {t('admin.impersonation.banner_reason', { reason: activeSession.reason })}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            loading={stopping}
            data-testid="impersonation-banner-end"
            aria-label={t('admin.impersonation.end_aria', {
              restaurant: activeSession.tenant_name,
            })}
            className="ms-auto min-h-11 min-w-11"
            onClick={() => void handleStop()}
          >
            {t('admin.impersonation.end')}
          </Button>
        </>
      ) : (
        <p className="text-sm text-start flex-1 min-w-0 basis-full sm:basis-0">
          {t('admin.impersonation.expired')}
        </p>
      )}
    </div>
  )
}

/** Pedido de foco no h1 após a rota nova montar (Shell faz o flush). */
export { requestFocusPageH1 as focusPageH1 }
